import { useEffect, useState, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Globe } from "lucide-react";
import { useTranslation } from "react-i18next";

const LOCALE_STORAGE_KEY = "uno_locale_v1";
const LOADER_SHOWN_KEY = "uno_loader_shown";

export const AppLoader = () => {
  const { i18n, t } = useTranslation();
  const locale = i18n.language;

  const [progress, setProgress] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [showLanguageSelect, setShowLanguageSelect] = useState(false);
  const [hasStoredLocale, setHasStoredLocale] = useState(false);
  const hasCheckedRef = useRef(false);

  const languageOptions = [
    { code: "ar", label: t("KEY.LANG_AR"), flag: "/iq-flag.svg" },
    { code: "en", label: t("KEY.LANG_EN"), flag: "/us-flag.svg" },
    { code: "ku", label: t("KEY.LANG_KU"), flag: "/ku-flag.svg" },
  ] as const;

  // On mount: determine if we should show language picker or just splash
  useEffect(() => {
    if (hasCheckedRef.current) return;
    hasCheckedRef.current = true;

    if (typeof window === "undefined") return;

    // Skip loader and force Arabic for the /menu page
    if (window.location.pathname === "/menu") {
      if (locale !== "ar") {
        i18n.changeLanguage("ar");
      }
      try {
        window.localStorage.setItem(LOCALE_STORAGE_KEY, "ar");
        window.sessionStorage.setItem(LOADER_SHOWN_KEY, "true");
      } catch {}
      setIsLoading(false);
      return;
    }

    // Check if loader was already shown in this session
    const loaderShown = window.sessionStorage.getItem(LOADER_SHOWN_KEY);
    if (loaderShown === "true") {
      setIsLoading(false);
      return;
    }

    // Check if user has a stored language preference
    try {
      const stored = window.localStorage.getItem(LOCALE_STORAGE_KEY);
      if (stored && languageOptions.some((lang) => lang.code === stored)) {
        setHasStoredLocale(true);
        if (stored !== locale) {
          i18n.changeLanguage(stored);
        }
      }
    } catch {
      // localStorage not available
    }
  }, [locale, i18n]);

  // Lock scroll while loader is visible
  useEffect(() => {
    if (!isLoading) return;

    const html = document.documentElement;
    const body = document.body;

    const prevHtmlOverflow = html.style.overflow;
    const prevHtmlHeight = html.style.height;
    const prevBodyOverflow = body.style.overflow;
    const prevBodyHeight = body.style.height;

    html.style.overflow = "hidden";
    html.style.height = "100%";
    body.style.overflow = "hidden";
    body.style.height = "100%";

    return () => {
      html.style.overflow = prevHtmlOverflow;
      html.style.height = prevHtmlHeight;
      body.style.overflow = prevBodyOverflow;
      body.style.height = prevBodyHeight;
    };
  }, [isLoading]);

  // Progress bar + auto-dismiss or language picker
  useEffect(() => {
    if (!isLoading) return;

    const progressInterval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          clearInterval(progressInterval);
          return 100;
        }
        return prev + Math.random() * 12;
      });
    }, 120);

    if (hasStoredLocale) {
      const autoDismissTimer = setTimeout(() => {
        clearInterval(progressInterval);
        setProgress(100);
        try {
          window.sessionStorage.setItem(LOADER_SHOWN_KEY, "true");
        } catch {}
        setIsLoading(false);
      }, 300); // Shaved more artificial delay!

      return () => {
        clearInterval(progressInterval);
        clearTimeout(autoDismissTimer);
      };
    } else {
      const showLangTimer = setTimeout(() => {
        clearInterval(progressInterval);
        setProgress(100);
        setShowLanguageSelect(true);
      }, 500); // Shaved artificial delay!

      return () => {
        clearInterval(progressInterval);
        clearTimeout(showLangTimer);
      };
    }
  }, [isLoading, hasStoredLocale]);

  const handleLanguageSelect = useCallback(
    (langCode: string) => {
      document.documentElement.style.overflow = "";
      document.documentElement.style.height = "";
      document.body.style.overflow = "";
      document.body.style.height = "";

      try {
        window.localStorage.setItem(LOCALE_STORAGE_KEY, langCode);
        window.sessionStorage.setItem(LOADER_SHOWN_KEY, "true");
      } catch {}

      if (langCode !== locale) {
        i18n.changeLanguage(langCode);
      }
      setIsLoading(false);
    },
    [locale, i18n],
  );

  const labels = {
    loading: t("APP_LOADER.LOADING"),
    welcome: t("APP_LOADER.WELCOME"),
    restaurantName: t("APP_LOADER.RESTAURANT_NAME"),
    tagline: t("APP_LOADER.TAGLINE"),
    selectLanguage: t("APP_LOADER.SELECT_LANGUAGE"),
    tapToContinue: t("APP_LOADER.TAP_TO_CONTINUE"),
  };

  return (
    <AnimatePresence mode="wait">
      {isLoading && (
        <motion.div
          key="loader"
          initial={{ opacity: 1 }}
          animate={{ opacity: 1 }}
          exit={{
            opacity: 0,
            scale: 1.05,
            filter: "blur(8px)",
            transition: { duration: 0.6, ease: "easeInOut" },
          }}
          className="fixed inset-0 z-[9999] flex flex-col items-center justify-center overflow-hidden bg-bodyBg"
        >
          <motion.div
            className="absolute inset-0"
            animate={{
              background: [
                "radial-gradient(circle at 20% 20%, rgba(71,129,189,0.12) 0%, transparent 40%), radial-gradient(circle at 80% 80%, rgba(71,129,189,0.08) 0%, transparent 42%)",
                "radial-gradient(circle at 80% 20%, rgba(71,129,189,0.1) 0%, transparent 38%), radial-gradient(circle at 15% 85%, rgba(71,129,189,0.08) 0%, transparent 42%)",
                "radial-gradient(circle at 20% 20%, rgba(71,129,189,0.12) 0%, transparent 40%), radial-gradient(circle at 80% 80%, rgba(71,129,189,0.08) 0%, transparent 42%)",
              ],
            }}
            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
          />

          <div className="relative flex flex-col items-center justify-center z-10">
            <motion.div
              className="absolute h-40 w-40 rounded-full sm:h-52 sm:w-52"
              style={{
                background:
                  "radial-gradient(circle, rgba(71,129,189,0.18) 0%, transparent 72%)",
              }}
              animate={{ scale: [1, 1.3, 1], opacity: [0.5, 0.8, 0.5] }}
              transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
            />

            <motion.div
              className="absolute h-36 w-36 rounded-full border border-[#4781bd]/20 sm:h-44 sm:w-44"
              animate={{ rotate: 360 }}
              transition={{ duration: 12, repeat: Infinity, ease: "linear" }}
            />

            <motion.div
              className="absolute h-32 w-32 rounded-full sm:h-40 sm:w-40"
              style={{
                border: "3px solid transparent",
                borderTop: "3px solid rgba(71,129,189,0.85)",
                borderRight: "3px solid rgba(71,129,189,0.35)",
              }}
              animate={{ rotate: 360 }}
              transition={{ duration: 2.4, repeat: Infinity, ease: "linear" }}
            />

            <motion.div
              className="absolute h-28 w-28 rounded-full sm:h-36 sm:w-36"
              style={{
                border: "2px solid transparent",
                borderBottom: "2px solid rgba(71,129,189,0.25)",
                borderLeft: "2px solid rgba(71,129,189,0.12)",
              }}
              animate={{ rotate: -360 }}
              transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
            />

            <motion.div
              className="relative flex h-28 w-28 items-center justify-center sm:h-36 sm:w-36"
              initial={{ scale: 0.85, opacity: 0, y: 18 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              transition={{
                type: "spring",
                stiffness: 180,
                damping: 16,
                delay: 0.2,
              }}
            >
              <motion.div className="absolute inset-x-4 bottom-1 h-5 rounded-full bg-[#4781bd]/10 blur-xl" />

              <motion.div
                className="absolute inset-0 rounded-[38px] border border-[#4781bd]/15 bg-white/70 shadow-[0_18px_40px_rgba(71,129,189,0.14)] backdrop-blur-sm"
                animate={{
                  boxShadow: [
                    "0 18px 40px rgba(71,129,189,0.12)",
                    "0 22px 46px rgba(71,129,189,0.2)",
                    "0 18px 40px rgba(71,129,189,0.12)",
                  ],
                }}
                transition={{
                  duration: 2.5,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              />

              <div className="absolute inset-3 rounded-[28px] border border-[#4781bd]/12" />

              <motion.div
                className="relative z-10 w-full h-full"
                animate={{ y: [0, -5, 0], scale: [1, 1.03, 1] }}
                transition={{
                  duration: 2.2,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              >
                <img
                  src="/images/logo.png"
                  alt="Restaurant Logo"
                  className="w-full h-full object-contain drop-shadow-md"
                />
              </motion.div>
            </motion.div>
          </div>

          <motion.div
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.6, duration: 0.7 }}
            className="relative z-10 text-center space-y-3 mt-12"
          >
            <motion.p
              animate={{ opacity: [0.4, 1, 0.4] }}
              transition={{
                duration: 2.5,
                repeat: Infinity,
                ease: "easeInOut",
              }}
              className="text-base tracking-wide text-[#4781bd]/70 sm:text-lg font-['PlutoRegular']"
            >
              {labels.welcome}
            </motion.p>
            <motion.h1
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.8, duration: 0.5, type: "spring" }}
              className="text-3xl font-bold tracking-tight text-[#4781bd] sm:text-4xl font-['PlutoRegular']"
            >
              {labels.restaurantName}
            </motion.h1>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1, duration: 0.5 }}
              className="text-sm font-light text-[#4781bd]/55 sm:text-base font-['PlutoRegular']"
            >
              {labels.tagline}
            </motion.p>
          </motion.div>

          <AnimatePresence mode="wait">
            {!showLanguageSelect ? (
              <motion.div
                key="progress"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0, y: -20 }}
                className="flex flex-col items-center"
              >
                <motion.div
                  initial={{ width: 0, opacity: 0 }}
                  animate={{ width: "220px", opacity: 1 }}
                  transition={{ delay: 1.2, duration: 0.5 }}
                  className="relative z-10 mt-10 h-1.5 w-56 overflow-hidden rounded-full bg-[#4781bd]/10 backdrop-blur-sm sm:w-60"
                >
                  <motion.div
                    className="absolute inset-y-0 left-0 rounded-full"
                    style={{
                      width: `${Math.min(progress, 100)}%`,
                      background:
                        "linear-gradient(90deg, rgba(71,129,189,0.7), rgba(71,129,189,1))",
                    }}
                    transition={{ duration: 0.3, ease: "easeOut" }}
                  />
                  <motion.div
                    className="absolute inset-y-0 left-0 w-full bg-linear-to-r from-transparent via-white/30 to-transparent"
                    animate={{ x: ["-100%", "200%"] }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      ease: "linear",
                    }}
                  />
                </motion.div>

                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 1.4, duration: 0.3 }}
                  className="relative z-10 mt-4 text-sm uppercase tracking-wider text-[#4781bd]/50 font-['PlutoRegular']"
                >
                  {labels.loading}
                </motion.p>
              </motion.div>
            ) : (
              <motion.div
                key="language-select"
                initial={{ opacity: 0, y: 30, scale: 0.9 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.5, ease: "easeOut" }}
                className="relative z-10 mt-10 flex flex-col items-center"
              >
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.2 }}
                  className="flex items-center gap-2 mb-6"
                >
                  <Globe className="h-5 w-5 text-[#4781bd]/70" />
                  <span className="text-sm tracking-wide text-[#4781bd]/70 font-['PlutoRegular']">
                    {labels.selectLanguage}
                  </span>
                </motion.div>

                <div className="flex flex-wrap justify-center gap-3">
                  {languageOptions.map((lang, index) => (
                    <motion.button
                      key={lang.code}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.3 + index * 0.1 }}
                      whileHover={{ scale: 1.05, y: -2 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => handleLanguageSelect(lang.code)}
                      className={`flex items-center gap-3 px-5 py-3 rounded-xl backdrop-blur-md transition-all duration-300 border ${
                        locale === lang.code
                          ? "bg-[#4781bd]/10 border-[#4781bd]/35 shadow-[0_12px_30px_rgba(71,129,189,0.16)]"
                          : "bg-white/55 border-[#4781bd]/15 hover:bg-white/75 hover:border-[#4781bd]/25"
                      }`}
                    >
                      <img
                        src={lang.flag}
                        alt={lang.label}
                        width={28}
                        height={20}
                        className="rounded shadow-sm"
                      />
                      <span className="text-sm font-medium text-[#4781bd] font-['PlutoRegular']">
                        {lang.label}
                      </span>
                    </motion.button>
                  ))}
                </div>

                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.8 }}
                  className="mt-6 text-xs text-[#4781bd]/45 font-['PlutoRegular']"
                >
                  {labels.tapToContinue}
                </motion.p>
              </motion.div>
            )}
          </AnimatePresence>



          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            {[...Array(12)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute h-1 w-1 rounded-full bg-[#4781bd]/30"
                style={{
                  top: `${Math.random() * 100}%`,
                  left: `${Math.random() * 100}%`,
                }}
                animate={{
                  y: [0, -30, 0],
                  x: [0, (Math.random() - 0.5) * 20, 0],
                  opacity: [0, 0.6, 0],
                  scale: [0, 1, 0],
                }}
                transition={{
                  duration: 2.5 + Math.random() * 2,
                  repeat: Infinity,
                  delay: Math.random() * 3,
                  ease: "easeInOut",
                }}
              />
            ))}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
